import React, { useState, useRef, useEffect, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import useTransactions from '@/hooks/useTransactions';
import { ElementPosition, TransactionType } from '@/types';
import { ReactComponent as SearchIcon } from "@/assets/icons/search.svg";
import { ReactComponent as CloseIcon } from "@/assets/icons/close.svg";
import TableList from '@/components/list/tableList/tableList';
import TableListRow from '@/components/list/tableList/tableListRow';
import TableListCell from '@/components/list/tableList/tableListCell';
import Callout from '@/components/callout/callout';
import SearchResultHolder from './SearchResultHolder';
import DateTime from '@/components/text/DateTime';

interface TransactionSelectorProps {
    storageId?: string;
    className?: string;
    selectedTransactionId?: string;
    onTransactionSelect?: (transactionId: string) => void;
    placeholder?: string;
    transactionType?: TransactionType; // Optional filter by type
    draftOnly?: boolean; // Optional filter: only draft, non-deleted transactions
    readOnly?: boolean; // If true, makes the selector read-only
}

const TransactionSelector: FC<TransactionSelectorProps> = ({ 
    storageId,
    className, 
    selectedTransactionId,
    onTransactionSelect,
    placeholder,
    transactionType,
    draftOnly,
    readOnly = false
}) => {
    const { t } = useTranslation();

    const [inFocus, setInFocus] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const TransactionSelectorWrapperRef = useRef<HTMLDivElement>(null);
    const TransactionSelectorResultsRef = useRef<HTMLDivElement>(null);
    const [inputLocation, setInputLocation] = useState<ElementPosition | null>(null);

    const {
        filteredTransactions,
        filterQuery,
        setFilterQuery,
        transactions
    } = useTransactions({ storageId });

    // Filter by transaction type and draft/deleted state if specified
    const displayTransactions = filteredTransactions
        .filter(t => !draftOnly || (t.isDraft === true && t.isFinalized === false && t.isDeleted !== true))
        .filter(t => !transactionType || t.type === transactionType);

    // Get selected transaction for display
    const selectedTransaction = selectedTransactionId 
        ? transactions?.find(transaction => transaction.id === selectedTransactionId) 
        : undefined;

    const handleOnClick = () => {
        setInFocus(true);
        if (TransactionSelectorWrapperRef.current) {
            const rect = TransactionSelectorWrapperRef.current.getBoundingClientRect();
            setInputLocation({
                top: rect.bottom,
                left: rect.left,
                width: rect.width
            });
        }
    };

    const handleOnBlur = () => {
        setInFocus(false);
    };

    const handleSearchIconClick = () => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    const location = useLocation();

    useEffect(() => {
        handleOnBlur();
        // eslint-disable-next-line
    }, [location]);

    // Update rect on window resize if inFocus
    useEffect(() => {
        if (!inFocus) return;
        const updateRect = () => {
            if (TransactionSelectorWrapperRef.current) {
                const rect = TransactionSelectorWrapperRef.current.getBoundingClientRect();
                setInputLocation({
                    top: rect.bottom,
                    left: rect.left,
                    width: rect.width
                });
            }
        };
        window.addEventListener('resize', updateRect);
        updateRect();
        return () => window.removeEventListener('resize', updateRect);
    }, [inFocus]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                TransactionSelectorWrapperRef.current && !TransactionSelectorWrapperRef.current.contains(target) &&
                TransactionSelectorResultsRef.current && !TransactionSelectorResultsRef.current.contains(target)
            ) {
                handleOnBlur();
            }
        };

        if (inFocus) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [inFocus]);

    const selectedItemPreview = selectedTransaction ? (
        <div className="mt-2 p-3 bg-gray-50 rounded-md flex items-center justify-between">
            <div>
                <div className="font-medium">
                    {`${selectedTransaction.transactionId}: ${selectedTransaction.type === TransactionType.INCOMING ? t('labelIncoming') : t('labelOutgoing')}`}
                </div>
                {selectedTransaction.description && (
                    <div className="text-sm text-gray-600">{selectedTransaction.description}</div>
                )}
                {selectedTransaction.customer?.customerName && (
                    <div className="text-sm text-gray-600">{t('labelCustomer')}: {selectedTransaction.customer.customerName}</div>
                )}
                {selectedTransaction.supplier?.supplierName && (
                    <div className="text-sm text-gray-600">{t('labelSupplier')}: {selectedTransaction.supplier.supplierName}</div>
                )}
            </div>
            {readOnly && <CloseIcon 
                className="cursor-pointer fill-gray-500 hover:fill-black"
                onClick={() => onTransactionSelect && onTransactionSelect('')}
            />}
        </div>
    ) : null;

    const transactionSelector = selectedTransaction ? null : (
        <div ref={TransactionSelectorWrapperRef} className={`relative ${className ? className : ''}`}>
            <div className="relative w-full">
                <SearchIcon 
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${storageId ? 'cursor-pointer' : 'cursor-not-allowed'} fill-gray-500`}
                    onClick={handleSearchIconClick}
                />
                <input 
                    type="text"
                    placeholder={inFocus ? (placeholder || t('placeholderSearchTransaction', 'Search transactions...')) : ''}
                    className={`
                        rounded-md
                        w-full
                        focus:outline-none
                        px-10
                        ${inFocus ? 'border-gray-500 border-b-white rounded-b-none' : ''}
                    `} 
                    value={inFocus ? filterQuery : ''}
                    onChange={(e) => setFilterQuery(e.target.value)}
                    data-ignore="true"
                    onFocus={handleOnClick}
                    ref={searchInputRef}
                    disabled={!storageId}
                />
                {inFocus && filterQuery.length > 0 && (
                    <CloseIcon 
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer fill-black" 
                        onClick={() => setFilterQuery('')}
                    />
                )}
            </div>
        </div>
    );

    const results = selectedTransaction ? null : inFocus && inputLocation && (
        <SearchResultHolder
            inputRef={inputLocation}
            resultsRef={TransactionSelectorResultsRef}
        >
            {filterQuery.length > 0 && !displayTransactions.length ? (
                <Callout type="error">{t('noResultsFound')}</Callout>
            ) : null}

            {displayTransactions.length > 0 ? (
                <TableList className="small-list hover-effect border-none rounded-t-none">
                    <TableListRow isHeader>
                        <TableListCell>{t('titleId', 'ID')}</TableListCell>
                        <TableListCell>{t('titleType', 'Type')}</TableListCell>
                        <TableListCell>{t('titleDescription', 'Description')}</TableListCell>
                        <TableListCell>{t('titleDate', 'Date')}</TableListCell>
                    </TableListRow>
                    {displayTransactions.map((transaction) => {
                        return (
                        <TableListRow
                            key={transaction.id}
                            onClick={() => {
                                onTransactionSelect && onTransactionSelect(transaction.id as string);
                                handleOnBlur();
                            }}
                            className="cursor-pointer"
                        >
                            <TableListCell>{transaction.transactionId}</TableListCell>
                            <TableListCell>
                                {transaction.type === TransactionType.INCOMING ? t('labelIncoming') : t('labelOutgoing')}
                            </TableListCell>
                            <TableListCell>{transaction.description || '-'}</TableListCell>
                            <TableListCell>
                                <DateTime timestamp={transaction.keyHistory?.createdAt} hideTimeOnSm hideTimeOnMd />
                            </TableListCell>
                        </TableListRow>
)})}
                </TableList>
            ) : null}
        </SearchResultHolder>
    );

    return (
        <>
            {/* Search Field */}
            {transactionSelector}
            {/* Search Results */}
            {results}
            {/* Selected Item */}
            {selectedItemPreview}
        </>
    );
};

export default TransactionSelector;
